import React, { useState, useEffect, useCallback } from "react";
import { FileText, Clock, Save, Eye, ArrowUpRight, History, Globe, Edit, Trash2, Plus, Search, Filter, RefreshCw, CheckCircle, AlertCircle, Calendar, Users, FileCheck, BookOpen, FileEdit, Send, MessagesSquare, Check, X, Shield, ChevronDown, User, Building2, Landmark, Briefcase, Flag, Mail, Phone, MessageCircle, MapPin, Menu, Link, Settings2, Building, FileInput, Layout, PenTool, Code, Facebook, Twitter, Instagram, Linkedin, Youtube, ExternalLink, Hash, Info } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import ContactInfoManagement from "./ContactInfoManagement";
import SocialMediaManagement from "./SocialMediaManagement";
import MenuLinkManagement from "./MenuLinkManagement";
import PageEditor from "./PageEditor";
import ContentTabs from "./ContentTabs";
// Import Content Management interfaces and enums
import {
  ContentManagementTypeEnum,
  ContentVersionStatusEnum,
  ContentReviewCycleEnum,
  ContentManagement,
  ContentManagementVersion,
  ContentManagementTranslation
} from "../../modules/shared/model/ContentManagementInterfaces";
// Import Page Management interfaces
// Import the Content Management hook
import { useContentManagement } from "../../modules/shared/hooks/useContentManagement";
// Import auth hook to get current user
import { useAppSelector, useAppDispatch } from "../../redux/store";
// Import getAllLanguages action
import { getAllLanguages } from "../../redux/Actions/AuthAction";
// Import translation actions
import {
  createContentManagementTranslation,
  updateContentManagementTranslation,
  deleteContentManagementTranslation
} from "../../redux/Actions/ContentManagementAction";

const TermsEditor = () => {
  const dispatch = useAppDispatch();

  // Content Management hook
  const {
    createVersion,
    createContent,
    getAllVersions,
    getContentByVersionId,
    getContentManagementTranslationsByContentId,
    getContentByType,
    updateContent,
    deleteContent,
    loading: contentLoading,
    error: contentError
  } = useContentManagement();

  // Get current user and languages from auth state
  const currentUser = useAppSelector((state) => state.auth.user);
  const languages = useAppSelector((state) => state.auth.languages);
  console.log(languages);
  // Get English language ID
  const englishLanguage = languages?.find((lang: any) =>
    lang.name?.toLowerCase() === "english"
  );
  console.log(englishLanguage);
  const englishLanguageId = englishLanguage?.id || "en"; // fallback to "en" if not found

  // State for processed versions data
  const [processedVersions, setProcessedVersions] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [contentByType, setContentByType] = useState({});
  const [translationsByContent, setTranslationsByContent] = useState({});
  const [showTranslationModal, setShowTranslationModal] = useState(false);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [selectedContentForTranslation, setSelectedContentForTranslation] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [selectedSettings, setSelectedSettings] = useState({
    language: { id: "" },
    reviewCycle: { id: 0 }
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [dashboardStats, setDashboardStats] = useState({
    totalVersions: 0,
    activeDocuments: 0,
    totalLanguages: 0,
    lastUpdated: null
  });
  // Fetch languages when component mounts
  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getAllLanguages({ isShowLoader: false }));
    }
  }, [languages, dispatch]);

  // Fetch and process all data when component mounts
  useEffect(() => {
    const loadAllData = async () => {
      if (isDataLoaded) return; // Prevent duplicate loading

      try {
        setIsDataLoaded(true);
        console.log("Loading all content management data...");

        // 1. Fetch all versions
        const versionsResponse = await getAllVersions();
        const versions = (versionsResponse as any)?.payload.data || [];

        if (!versions || versions.length === 0) {
          console.log("No versions found");
          return;
        }

        // 2. Process each version sequentially to avoid overwhelming the API
        const processedData = [];
        console.log(versions, "versions");
        for (const version of versions) {
          try {
            // Fetch content for this version
            const contentResponse = await getContentByVersionId(version.id);
            const content = (contentResponse as any)?.payload || [];

            // Process content and fetch translations
            const processedContent = [];
            for (const contentItem of content) {
              try {
                const translationsResponse = await getContentManagementTranslationsByContentId(contentItem.id);
                const contentTranslations = (translationsResponse as any)?.payload || [];

                processedContent.push({
                  ...contentItem,
                  translations: contentTranslations
                });
              } catch (error) {
                console.error(`Failed to fetch translations for content ${contentItem.id}:`, error);
                processedContent.push({
                  ...contentItem,
                  translations: []
                });
              }
            }

            // Transform version data
            const processedVersion = {
              id: version.id,
              version: version.name || "1.0",
              type: getDocumentTypeFromContent(processedContent),
              status: getStatusFromVersion(version.status),
              publishDate: version.lastModified,
              effectiveDate: version.effectiveDate,
              language: "English",
              lastModified: version.lastModified,
              modifiedBy: "Admin",
              sections: extractSectionsFromContent(processedContent),
              changelog: generateChangelog(version),
              translations: extractTranslationLanguages(processedContent),
              reviewStatus: { legal: "pending", compliance: "approved" },
              ...extractAdditionalFields(processedContent)
            };

            processedData.push(processedVersion);
          } catch (error) {
            console.error(`Failed to process version ${version.id}:`, error);
          }
        }

        console.log(processedData, "processedData");
        setProcessedVersions(processedData);
        console.log(`Successfully processed ${processedData.length} versions`);

      } catch (error) {
        console.error("Failed to load content management data:", error);
        setIsDataLoaded(false); // Allow retry
      }
    };

    loadAllData();
  }, []); // Empty dependency array - only run once on mount

  // Manual refresh function
  const refreshData = async () => {
    setIsDataLoaded(false);
    setProcessedVersions([]);
    // The useEffect will run again because isDataLoaded changed
  };

  // Helper methods for processing data
  const getDocumentTypeFromContent = (content: ContentManagement[]) => {
    if (!content || content.length === 0) return "Terms_Conditions";
    const firstContent = content[0];
    return getDocumentTypeName(firstContent.type?.id);
  };

  const getDocumentTypeName = (typeId: number) => {
    switch (typeId) {
      case ContentManagementTypeEnum.Terms_Conditions: return "Terms_Conditions";
      case ContentManagementTypeEnum.Privacy_Policy: return "Privacy_Policy";
      case ContentManagementTypeEnum.Professional_Terms: return "Professional_Terms";
      case ContentManagementTypeEnum.Emergency_Services: return "Emergency_Services";
      case ContentManagementTypeEnum.Customer_Information: return "Customer_Information";
      case ContentManagementTypeEnum.Professional_Information: return "Professional_Information";
      default: return "Terms_Conditions";
    }
  };

  // Get enum key name from enum value
  const getEnumKeyName = (enumValue: number) => {
    switch (enumValue) {
      case ContentManagementTypeEnum.Terms_Conditions: return "Terms_Conditions";
      case ContentManagementTypeEnum.Privacy_Policy: return "Privacy_Policy";
      case ContentManagementTypeEnum.Professional_Terms: return "Professional_Terms";
      case ContentManagementTypeEnum.Emergency_Services: return "Emergency_Services";
      case ContentManagementTypeEnum.Customer_Information: return "Customer_Information";
      case ContentManagementTypeEnum.Professional_Information: return "Professional_Information";
      default: return null;
    }
  };

  const getStatusFromVersion = (status: any) => {
    if (!status || !status.id) return "draft";
    switch (status.id) {
      case ContentVersionStatusEnum.Draft: return "draft";
      case ContentVersionStatusEnum.Published: return "published";
      case ContentVersionStatusEnum.Archived: return "archived";
      default: return "draft";
    }
  };

  const extractSectionsFromContent = (content: ContentManagement[]) => {
    if (!content || content.length === 0) return [];

    const sections = [];
    content.forEach((contentItem, index) => {
      if (contentItem.contentTitle && contentItem.contentDescription) {
        sections.push({
          id: index + 1,
          contentTitle: contentItem.contentTitle,
          contentDescription: contentItem.contentDescription
        });
      }
    });

    return sections;
  };

  const generateChangelog = (version: ContentManagementVersion) => {
    return [{
      date: version.lastModified || new Date().toISOString(),
      author: "Admin", // Default author
      changes: ["Document created or updated"]
    }];
  };

  const extractTranslationLanguages = (content: ContentManagement[]) => {
    const languages = new Set();
    content.forEach((contentItem: any) => {
      if (contentItem.translations && Array.isArray(contentItem.translations)) {
        contentItem.translations.forEach((translation: any) => {
          if (translation.language?.name) {
            languages.add(translation.language.name);
          }
        });
      }
    });
    return Array.from(languages);
  };

  const extractAdditionalFields = (content: ContentManagement[]) => {
    // Extract country, menuPlacement, userAccess if available
    const firstContent = content[0];
    if (!firstContent) return {};

    return {
      country: (firstContent as any).country || null,
      menuPlacement: (firstContent as any).menuPlacement || null,
      userAccess: (firstContent as any).userAccess || null
    };
  };

  const [showAddModal, setShowAddModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLanguage, setSelectedLanguage] = useState("en");


  // Union type for activeTab to handle both enum values and additional tabs
  type ActiveTabType = ContentManagementTypeEnum | "contact" | "menu" | "social";
  const [activeTab, setActiveTab] = useState<ActiveTabType>(ContentManagementTypeEnum.Terms_Conditions);
  const [activeDocument, setActiveDocument] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [contentTab, setContentTab] = useState("content");

  // Reset contentTab when activeTab changes (only for content types, not special tabs)
  useEffect(() => {
    // Only reset contentTab for actual content management types, not special tabs like "contact", "menu", "social"
    if (typeof activeTab === "number") {
      setContentTab("content");
    }
  }, [activeTab]);
  const [expandedSection, setExpandedSection] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("all");
  const layouts = [
    { id: "default", name: "Default Layout", description: "Standard page layout with sidebar" },
    { id: "full", name: "Full Width", description: "Full width layout without sidebar" },
    { id: "landing", name: "Landing Page", description: "Special layout for landing pages" }
  ];

  const [documentContent, setDocumentContent] = useState({
    [ContentManagementTypeEnum.Terms_Conditions]: {},
    [ContentManagementTypeEnum.Privacy_Policy]: {},
    [ContentManagementTypeEnum.Professional_Terms]: {},
    [ContentManagementTypeEnum.Emergency_Services]: {},
    [ContentManagementTypeEnum.Customer_Information]: {},
    [ContentManagementTypeEnum.Professional_Information]: {}
  });

  // Update documentContent when contentByType changes
  useEffect(() => {
    setDocumentContent(prev => ({
      ...prev,
      ...contentByType
    }));
  }, [contentByType]);



  // Social Media URLs - Default fallback values
  const [socialMediaUrls, setSocialMediaUrls] = useState({
    facebook: "",
    twitter: "",
    instagram: "",
    linkedin: "",
    youtube: ""
  });

  const documentTypes = [
    { id: ContentManagementTypeEnum.Terms_Conditions, name: "Terms & Conditions", icon: BookOpen, description: "General terms of service" },
    { id: ContentManagementTypeEnum.Privacy_Policy, name: "Privacy Policy", icon: Shield, description: "Data privacy and protection" },
    { id: ContentManagementTypeEnum.Professional_Terms, name: "Professional Terms", icon: FileCheck, description: "Terms for service providers" },
    { id: ContentManagementTypeEnum.Emergency_Services, name: "Emergency Services", icon: AlertCircle, description: "Emergency service terms" },
    { id: ContentManagementTypeEnum.Customer_Information, name: "Customer Information", icon: User, description: "Tax and other information for customers" },
    { id: ContentManagementTypeEnum.Professional_Information, name: "Professional Information", icon: Briefcase, description: "Tax and other information for professionals" }
  ];

  const countries = [
    { id: "all", name: "All Countries" },
    { id: "ca", name: "Canada" },
    { id: "us", name: "United States" },
    { id: "uk", name: "United Kingdom" },
    { id: "au", name: "Australia" },
    { id: "fr", name: "France" },
    { id: "de", name: "Germany" },
    { id: "it", name: "Italy" },
    { id: "es", name: "Spain" }
  ];

  const menuOptions = [
    { id: "none", name: "No Menu Display" },
    { id: "guest", name: "Non-Registered Users Only" },
    { id: "registered", name: "Registered Users Only" },
    { id: "both", name: "Both User Types" }
  ];

  const userAccessOptions = [
    { id: "all", name: "All Users" },
    { id: "customer", name: "Customers Only" },
    { id: "professional", name: "Professionals Only" }
  ];

  const additionalTabs = [
    { id: "contact" as const, label: "Contact Information", icon: Phone },
    { id: "menu" as const, label: "Menu & Footer", icon: Menu },
    { id: "social" as const, label: "Social Media", icon: Link }
  ];

  const DocumentModal = ({ version, isOpen, onClose }) => {
    const [versionName, setVersionName] = useState("");
    const [selectedType, setSelectedType] = useState(ContentManagementTypeEnum.Terms_Conditions);

    const handleSubmit = async () => {
      try {
        if (!currentUser?.id) {
          alert("User not authenticated");
          return;
        }

        if (!versionName.trim()) {
          alert("Please enter a version name");
          return;
        }

        // 1. Create Content Management Version
        const versionPayload = {
          name: versionName,
          status: { id: ContentVersionStatusEnum.Published },
          user: { id: currentUser.id },
          effectiveDate: new Date().toISOString(),
          publishDate: new Date().toISOString()
        };

        const createdVersion = await createVersion(versionPayload).unwrap();

        // 2. Create Content Management with selected type
        const contentPayload = {
          version: { id: createdVersion.id },
          type: { id: selectedType },
          settingLanguage: { id: englishLanguageId },
        };

        console.log("Creating content with language ID:", englishLanguageId);
        console.log("Available languages:", languages);
        console.log("English language found:", englishLanguage);

        await createContent(contentPayload);

        // Reset form and close modal
        setVersionName("");
        setSelectedType(ContentManagementTypeEnum.Terms_Conditions);
        onClose();

      } catch (error) {
        console.error("Failed to create document:", error);
        alert("Failed to create document. Please try again.");
      }
    };

    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl w-full max-w-2xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">{version ? "Edit Document" : "New Document"}</h2>
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
              <select
                className="w-full px-3 py-2 border rounded-lg"
                value={selectedType}
                onChange={(e) => setSelectedType(parseInt(e.target.value))}
              >
                {documentTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Version Name</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter version name (e.g., 1.0, 2.0)"
                value={versionName}
                onChange={(e) => setVersionName(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={contentLoading || !versionName.trim()}
              >
                {contentLoading ? "Creating..." : "Create Document"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };




  const VersionCard = ({ version }) => (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start">
        <div className="mb-4 sm:mb-0">
          <div className="flex flex-wrap items-center gap-2">
            {/* <h3 className="text-base sm:text-lg font-semibold">Version {version.version}</h3> */}
            <Badge variant={undefined} className={`text-xs sm:text-sm ${version.status === "published" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {version.status.charAt(0).toUpperCase() + version.status.slice(1)}
            </Badge>
            <Badge variant="outline" className="text-xs sm:text-sm">
              {documentTypes.find(t => t.id === version.type)?.name || version.type.toUpperCase()}
            </Badge>
            {version.country && <Badge variant={undefined} className="text-xs sm:text-sm bg-purple-100 text-purple-700">{version.country}</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs sm:text-sm text-gray-600">
            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
            Last modified: {version.lastModified}
            <span className="text-gray-400 hidden sm:inline">|</span>
            <Users className="h-3 w-3 sm:h-4 sm:w-4" />
            By: {version.modifiedBy}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => setSelectedVersion(version)}>
            <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setShowPreview(true)}>
            <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        </div>
      </div>
      {version.status === "published" && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Publish Date</div>
            <div className="font-medium text-sm sm:text-base">{version.publishDate}</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Effective Date</div>
            <div className="font-medium text-sm sm:text-base">{version.effectiveDate}</div>
          </div>
        </div>
      )}
      {(version.type === ContentManagementTypeEnum.Customer_Information || version.type === ContentManagementTypeEnum.Professional_Information) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">Menu Placement</div>
            <div className="font-medium text-sm sm:text-base">
              {menuOptions.find(m => m.id === version.menuPlacement)?.name || "Not Set"}
            </div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm text-gray-500">User Access</div>
            <div className="font-medium text-sm sm:text-base">
              {userAccessOptions.find(u => u.id === version.userAccess)?.name || "Not Set"}
            </div>
          </div>
        </div>
      )}
      <div className="mt-4 sm:mt-6">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Content Sections</h4>
        <div className="space-y-3">
          {version.sections.map(section => (
            <div key={section.id} className="p-3 bg-gray-50 rounded-lg">
              <h5 className="font-medium text-sm sm:text-base">{section.contentTitle}</h5>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 line-clamp-2">{section.contentDescription}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4 sm:mt-6">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Review Status</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm">Legal Review</div>
            <Badge variant={undefined} className={`text-xs sm:text-sm ${version.reviewStatus.legal === "approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {version.reviewStatus.legal.charAt(0).toUpperCase() + version.reviewStatus.legal.slice(1)}
            </Badge>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="text-xs sm:text-sm">Compliance Review</div>
            <Badge variant={undefined} className={`text-xs sm:text-sm ${version.reviewStatus.compliance === "approved" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
              {version.reviewStatus.compliance.charAt(0).toUpperCase() + version.reviewStatus.compliance.slice(1)}
            </Badge>
          </div>
        </div>
      </div>
      <div className="mt-4 sm:mt-6">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Available Translations</h4>
        <div className="flex flex-wrap gap-2">
          {version.translations.length > 0 ? (
            version.translations.map((lang, index) => (
              <Badge key={index} variant="outline" className="text-xs sm:text-sm">{lang}</Badge>
            ))
          ) : (
            <span className="text-xs sm:text-sm text-gray-500">No translations available</span>
          )}
        </div>
      </div>
      <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t">
        <h4 className="font-medium mb-3 text-sm sm:text-base">Recent Changes</h4>
        <div className="space-y-3">
          {version.changelog.map((log, index) => (
            <div key={index} className="text-xs sm:text-sm">
              <div className="flex flex-wrap items-center gap-2 text-gray-600">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />{log.date}
                <span className="text-gray-400 hidden sm:inline">|</span>
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />{log.author}
              </div>
              <ul className="mt-1 space-y-1 ml-4 sm:ml-6 list-disc text-gray-600">
                {log.changes.map((change, changeIndex) => (
                  <li key={changeIndex}>{change}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );


  const MenuItemModal = ({ item, isOpen, onClose }) => {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl w-full max-w-2xl">
          <div className="p-4 sm:p-6 border-b">
            <div className="flex items-center justify-between">
              <h2 className="text-lg sm:text-xl font-semibold">{item ? "Edit Menu Item" : "Add Menu Item"}</h2>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
          <div className="p-4 sm:p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input type="text" className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm" defaultValue={item?.title} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input type="text" className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm" defaultValue={item?.url} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <select className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm" defaultValue={item?.location}>
                  <option value="header">Header Menu</option>
                  <option value="footer">Footer</option>
                  <option value="sidebar">Sidebar Menu</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Visibility</label>
                <select className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm" defaultValue={item?.visibility}>
                  <option value="all">All Users</option>
                  <option value="guest">Guest Only</option>
                  <option value="registered">Registered Users</option>
                  <option value="professional">Professionals Only</option>
                  <option value="customer">Customers Only</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
              <input type="number" className="w-full px-2 py-1 sm:px-3 sm:py-2 border rounded-lg text-sm" defaultValue={item?.order} min="1" />
            </div>
            <div className="pt-4 border-t flex justify-end gap-3">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button>Save Changes</Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSocialMediaUpdate = useCallback((urls: any) => {
    setSocialMediaUrls(urls);
  }, []);

  const renderSocialContent = () => (
    <SocialMediaManagement />
  );


  const renderMenuEditor = () => (
    <MenuLinkManagement />
  );

  const renderMainContent = React.useCallback(() => {
    switch (activeTab) {
      case "contact":
        return <ContactInfoManagement />;
      case "menu":
        return renderMenuEditor();
      case "social":
        return renderSocialContent();
      case ContentManagementTypeEnum.Customer_Information:
        return (
          <div className="space-y-6">
            <ContentTabs
              documentType={activeTab}
              contentByType={contentByType}
              setContentByType={setContentByType}
              translationsByContent={translationsByContent}
              setTranslationsByContent={setTranslationsByContent}
              languages={languages}
              currentUser={currentUser}
              createContent={createContent}
              updateContent={updateContent}
              deleteContent={deleteContent}
              getContentByType={getContentByType}
              getContentManagementTranslationsByContentId={getContentManagementTranslationsByContentId}
              englishLanguageId={englishLanguageId}
            />
            <PageEditor />
          </div>
        );
      default:
        return (
          <ContentTabs
            documentType={activeTab}
            contentByType={contentByType}
            setContentByType={setContentByType}
            translationsByContent={translationsByContent}
            setTranslationsByContent={setTranslationsByContent}
            languages={languages}
            currentUser={currentUser}
            createContent={createContent}
            updateContent={updateContent}
            deleteContent={deleteContent}
            getContentByType={getContentByType}
            getContentManagementTranslationsByContentId={getContentManagementTranslationsByContentId}
            englishLanguageId={englishLanguageId}
          />
        );
    }
  }, [activeTab, contentByType, translationsByContent, languages, currentUser, createContent, updateContent, deleteContent, getContentByType, getContentManagementTranslationsByContentId, englishLanguageId]);

  // Reset modal states when activeTab changes
  useEffect(() => {
    setShowAddModal(false);
    setSelectedVersion(null);
    setShowPreview(false);
    setShowTranslationModal(false);
    setSelectedTranslation(null);
    setSelectedContentForTranslation(null);
  }, [activeTab]);

  // Fetch content by type when activeTab changes
  useEffect(() => {
    const fetchContentByType = async () => {
      // Skip if it's a special tab (contact, menu, social)
      if (typeof activeTab === "string" && ["contact", "menu", "social"].includes(activeTab)) {
        return;
      }

      // Skip if it's not a valid content type enum
      if (typeof activeTab !== "number") {
        return;
      }

      try {
        console.log(`Fetching content for type: ${activeTab}`);
        const contentResponse: any = await getContentByType(Number(activeTab));
        console.log(`Raw content response for type ${activeTab}:`, contentResponse);

        // Handle different response structures
        const content = contentResponse.payload?.data || [];

        // Ensure content is an array
        if (!Array.isArray(content)) {
          console.warn(`Content for type ${activeTab} is not an array:`, content);
          return;
        }

        // Transform content into the format expected by documentContent
        const transformedContent = {};
        const translationsData = {};

        // Fetch translations for each content item
        for (const item of content) {
          const key = item.id;
          console.log(`Processing content item ${key}:`, item);

          transformedContent[key] = {
            // Preserve all ContentManagement interface properties
            id: item.id,
            version: item.version,
            type: item.type,
            reviewCycle: item.reviewCycle,
            autoSave: item.autoSave,
            settingLanguage: item.settingLanguage,
            isText: item.isText,
            contentDescription: item.contentDescription || "",
            contentTitle: item.contentTitle || "",
            // Preserve any other properties that might exist
            ...item
          };

          // Fetch translations for this content item
          try {
            const translationsResponse = await getContentManagementTranslationsByContentId(item.id);
            const translations = (translationsResponse as any)?.payload || [];
            translationsData[key] = translations;
            console.log(`Translations for content ${key}:`, translations);
          } catch (error) {
            console.error(`Failed to fetch translations for content ${key}:`, error);
            translationsData[key] = [];
          }
        }

        setContentByType(prev => ({
          ...prev,
          [activeTab]: transformedContent
        }));

        setTranslationsByContent(prev => ({
          ...prev,
          [activeTab]: translationsData
        }));

        console.log(`Content fetched for type ${activeTab}:`, transformedContent);
        console.log(`Translations fetched for type ${activeTab}:`, translationsData);
      } catch (error) {
        console.error(`Failed to fetch content for type ${activeTab}:`, error);
      }
    };

    fetchContentByType();
  }, [activeTab]);

  // Auto-fetch content for the first tab on page load
  useEffect(() => {
    const fetchInitialContent = async () => {
      if (typeof activeTab === "number") {
        try {
          const contentResponse: any = await getContentByType(Number(activeTab));
          const content = contentResponse.payload?.data || [];

          if (Array.isArray(content)) {
            const transformedContent = {};
            const translationsData = {};

            for (const item of content) {
              const key = item.id;
              transformedContent[key] = {
                id: item.id,
                version: item.version,
                type: item.type,
                reviewCycle: item.reviewCycle,
                autoSave: item.autoSave,
                settingLanguage: item.settingLanguage,
                isText: item.isText,
                contentDescription: item.contentDescription || "",
                contentTitle: item.contentTitle || "",
                ...item
              };

              try {
                const translationsResponse = await getContentManagementTranslationsByContentId(item.id);
                const contentTranslations = (translationsResponse as any)?.payload || [];
                translationsData[key] = contentTranslations;
              } catch (error) {
                console.error(`Failed to fetch translations for content ${key}:`, error);
                translationsData[key] = [];
              }
            }

            setContentByType(prev => ({
              ...prev,
              [activeTab]: transformedContent
            }));

            setTranslationsByContent(prev => ({
              ...prev,
              [activeTab]: translationsData
            }));
          }
        } catch (error) {
          console.error(`Failed to fetch initial content for type ${activeTab}:`, error);
        }
      }
    };

    fetchInitialContent();
  }, []); // Run only once on mount

  const TranslationModal = ({ contentItem, translation, isOpen, onClose }) => {
    console.log("contentItem", contentItem, "translation", translation, "isOpen", isOpen, "onClose", onClose);
    const dispatch = useAppDispatch();
    const [formData, setFormData] = useState({
      content: translation?.content || "",
      title: translation?.title || "",
      language: { id: translation?.language?.id || "" },
      contentManagementType: { id: contentItem?.id }
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [languageError, setLanguageError] = useState("");

    // Check if language is already used for this content item
    const checkLanguageConflict = () => {
      // Only check for conflicts when creating new translations, not when updating
      if (translation) return false;

      if (!contentItem || !formData.language.id) return false;

      const contentTranslations = translationsByContent[activeTab]?.[contentItem.id] || [];
      const existingTranslation = contentTranslations.find(
        (t: any) => t.language?.id === formData.language.id
      );

      return existingTranslation;
    };

    const handleSubmit = async () => {
      try {
        // Clear previous errors
        setLanguageError("");

        if (!formData.language.id) {
          alert("Please select a language");
          return;
        }

        if (!formData.title.trim()) {
          alert("Please enter a title");
          return;
        }

        if (!formData.content.trim()) {
          alert("Please enter content");
          return;
        }

        // Check for language conflict (only when creating new translations)
        if (checkLanguageConflict()) {
          setLanguageError("A translation for this language already exists. Please select a different language or edit the existing translation.");
          return;
        }

        setIsSubmitting(true);

        if (translation) {
          // Update existing translation
          console.log("Updating translation:", translation.id, formData);
          await dispatch(updateContentManagementTranslation({
            id: translation.id,
            data: {
              content: formData.content,
              title: formData.title,
              language: { id: formData.language.id },
              contentManagementType: { id: formData.contentManagementType.id }
            }
          })).unwrap();

          setSuccessMessage("Translation updated successfully!");
        } else {
          // Create new translation
          console.log("Creating new translation:", formData);
          await dispatch(createContentManagementTranslation({
            content: formData.content,
            title: formData.title,
            language: { id: formData.language.id },
            contentManagementType: { id: formData.contentManagementType.id }
          })).unwrap();

          setSuccessMessage("Translation created successfully!");
        }

        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);

        // Close modal and refresh translations
        onClose();

        // Refresh translations for the current content type
        await refreshTranslations();

      } catch (error) {
        console.error("Failed to save translation:", error);
        alert(`Failed to save translation: ${error.message || "Please try again."}`);
      } finally {
        setIsSubmitting(false);
      }
    };

    // Reset form when modal opens/closes or translation changes
    useEffect(() => {
      if (isOpen) {
        setFormData({
          content: translation?.content || "",
          title: translation?.title || "",
          language: { id: translation?.language?.id || "" },
          contentManagementType: { id: contentItem?.id }
        });
        setLanguageError("");
      }
    }, [isOpen, translation, contentItem, activeTab]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" style={{ textAlign: "left" }}>
        <div className="bg-white rounded-xl w-full max-w-2xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {translation ? "Edit Translation" : "Add Translation"}
            </h2>
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                className={`w-full px-3 py-2 border rounded-lg ${languageError ? "border-red-500" : ""}`}
                value={formData.language.id}
                onChange={(e) => {
                  setFormData({ ...formData, language: { id: e.target.value } });
                  setLanguageError(""); // Clear error when language changes
                }}
                disabled={isSubmitting}
              >
                <option value="">Select Language</option>
                {languages?.map((lang: any) => (
                  <option key={lang.id} value={lang.id}>{lang.name}</option>
                ))}
              </select>
              {languageError && (
                <p className="text-red-500 text-sm mt-1">{languageError}</p>
              )}
              {!translation && (
                <p className="text-gray-500 text-xs mt-1">
                  Note: Language conflicts are checked when creating new translations
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
              <input
                type="text"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Enter translation title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                disabled={isSubmitting}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                rows={6}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                placeholder="Enter translated content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                disabled={isSubmitting}
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : (translation ? "Update Translation" : "Add Translation")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Function to refresh translations for the current content type
  const refreshTranslations = async () => {
    if (typeof activeTab === "string" && ["contact", "menu", "social"].includes(activeTab)) {
      return;
    }

    try {
      console.log("Refreshing translations for type:", activeTab);

      // Get current content for the active tab
      const currentContent = contentByType[activeTab] || {};

      // Fetch fresh translations for each content item
      const translationsData = {};

      for (const [contentId, contentItem] of Object.entries(currentContent)) {
        try {
          console.log(`Fetching translations for content ${contentId}:`, contentItem);

          // Fetch translations for this specific content management record
          const translationsResponse = await getContentManagementTranslationsByContentId(contentId);
          const translations = (translationsResponse as any)?.payload || [];

          console.log(`Translations for content ${contentId}:`, translations);
          translationsData[contentId] = translations;
        } catch (error) {
          console.error(`Failed to fetch translations for content ${contentId}:`, error);
          translationsData[contentId] = [];
        }
      }

      setTranslationsByContent(prev => ({
        ...prev,
        [activeTab]: translationsData
      }));

      console.log("Translations refreshed for type:", activeTab, "Data:", translationsData);
    } catch (error) {
      console.error("Failed to refresh translations:", error);
    }
  };

  // Function to get translation count for a specific content item
  const getTranslationCount = (contentId: string) => {
    const translations = translationsByContent[activeTab]?.[contentId] || [];
    return translations.length;
  };

  // Function to get available languages for a content item
  const getAvailableLanguages = (contentId: string) => {
    const translations = translationsByContent[activeTab]?.[contentId] || [];
    return translations.map((t: any) => t.language?.id).filter(Boolean);
  };

  // Function to get missing languages for a content item
  const getMissingLanguages = (contentId: string) => {
    const availableLanguages = getAvailableLanguages(contentId);
    const allLanguages = languages || [];

    return allLanguages.filter((lang: any) => !availableLanguages.includes(lang.id));
  };

  // Function to delete translation
  const handleDeleteTranslation = async (translationId: string) => {
    if (!confirm("Are you sure you want to delete this translation? This action cannot be undone.")) {
      return;
    }

    try {
      console.log("Deleting translation:", translationId);
      await dispatch(deleteContentManagementTranslation({ id: translationId })).unwrap();

      // Show success message
      setSuccessMessage("Translation deleted successfully!");
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Refresh translations after successful deletion
      await refreshTranslations();

      console.log("Translation deleted successfully");
    } catch (error) {
      console.error("Failed to delete translation:", error);
      alert(`Failed to delete translation: ${error.message || "Please try again."}`);
    }
  };

  // Function to save settings for all content items of the current type
  const handleSaveSettings = async () => {
    if (!selectedSettings?.language?.id || !selectedSettings?.reviewCycle?.id) {
      alert("Please select both language and review cycle");
      return;
    }

    try {
      setIsSavingSettings(true);
      console.log("Saving settings:", selectedSettings);

      // Get all content items for the current type
      const currentContent = contentByType[activeTab] || {};
      const contentIds = Object.keys(currentContent);

      if (contentIds.length === 0) {
        alert("No content items found to update");
        return;
      }

      // Update each content item with the new settings
      const updatePromises = contentIds.map(async (contentId) => {
        const contentItem = currentContent[contentId];

        try {
          await updateContent(contentId, {
            ...contentItem,
            type: { id: Number(contentItem?.type?.id) },
            settingLanguage: { id: selectedSettings.language.id },
            reviewCycle: { id: Number(selectedSettings.reviewCycle.id) }
          });

          console.log(`Updated content ${contentId} with new settings`);
        } catch (error) {
          console.error(`Failed to update content ${contentId}:`, error);
          throw error;
        }
      });

      // Wait for all updates to complete
      await Promise.all(updatePromises);

      // Show success message
      setSuccessMessage("Settings updated successfully for all content items!");
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);

      // Refresh content to show updated settings
      await refreshContentData();

      console.log("All settings updated successfully");
    } catch (error) {
      console.error("Failed to save settings:", error);
      alert(`Failed to save settings: ${error.message || "Please try again."}`);
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Function to refresh content data after settings update
  const refreshContentData = async () => {
    try {
      const contentResponse: any = await getContentByType(Number(activeTab));
      const content = contentResponse.payload?.data || [];

      if (Array.isArray(content)) {
        const transformedContent = {};
        const translationsData = {};

        for (const item of content) {
          const key = item.id;
          transformedContent[key] = {
            ...item
          };

          // Fetch translations for this content item
          try {
            const translationsResponse = await getContentManagementTranslationsByContentId(item.id);
            const translations = (translationsResponse as any)?.payload || [];
            translationsData[key] = translations;
          } catch (error) {
            console.error(`Failed to fetch translations for content ${key}:`, error);
            translationsData[key] = [];
          }
        }

        setContentByType(prev => ({
          ...prev,
          [activeTab]: transformedContent
        }));

        setTranslationsByContent(prev => ({
          ...prev,
          [activeTab]: translationsData
        }));
      }
    } catch (error) {
      console.error("Failed to refresh content data:", error);
    }
  };

  // Function to initialize settings from current content
  const initializeSettings = () => {
    const currentContent = contentByType[activeTab] || {};
    const contentItems = Object.values(currentContent);

    if (contentItems.length > 0) {
      // Use the first content item's settings as default
      const firstContent = contentItems[0] as any;
      setSelectedSettings({
        language: { id: firstContent.settingLanguage?.id || "" },
        reviewCycle: { id: Number(firstContent.reviewCycle?.id) || 0 }
      });
    }
  };

  // Initialize settings when activeTab changes
  useEffect(() => {
    if (typeof activeTab === "number" && !["contact", "menu", "social"].includes(activeTab.toString())) {
      initializeSettings();
    }
  }, [activeTab, contentByType]);

  // Function to fetch dashboard statistics
  const fetchDashboardStats = async () => {
    try {
      // Fetch total versions
      const versionsResponse = await getAllVersions();
      const totalVersions = (versionsResponse as any)?.payload?.data?.length || 0;

      // Calculate active documents (content items)
      const currentContent = contentByType[activeTab] || {};
      const activeDocuments = Object.keys(currentContent).length;

      // Get total languages
      const totalLanguages = languages?.length || 0;

      // Get last updated timestamp
      let lastUpdated = null;
      if (totalVersions > 0) {
        const versions = (versionsResponse as any)?.payload?.data || [];
        const sortedVersions = [...versions].sort((a: any, b: any) =>
          new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
        );
        lastUpdated = sortedVersions[0]?.lastModified;
      }

      setDashboardStats({
        totalVersions,
        activeDocuments,
        totalLanguages,
        lastUpdated
      });

      console.log("Dashboard stats updated:", {
        totalVersions,
        activeDocuments,
        totalLanguages,
        lastUpdated
      });
    } catch (error) {
      console.error("Failed to fetch dashboard stats:", error);
    }
  };

  // Function to format time ago
  const formatTimeAgo = (timestamp: string) => {
    if (!timestamp) return "Never";

    const now = new Date();
    const date = new Date(timestamp);
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInDays > 0) {
      return `${diffInDays}d ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours}h ago`;
    } else if (diffInMinutes > 0) {
      return `${diffInMinutes}m ago`;
    } else {
      return "Just now";
    }
  };

  // Fetch dashboard stats when component mounts or when content changes
  useEffect(() => {
    fetchDashboardStats();
  }, [contentByType, languages]);

  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />
      <main className="lg:pl-64 pt-16">
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Content Management</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Manage legal documents, contact information, and menu items</p>
              </div>
              {(activeTab !== "contact" && activeTab !== "menu" && activeTab !== "social") && (
                <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto flex items-center gap-2 text-sm sm:text-base">
                  <Plus className="h-3 w-3 sm:h-4 sm:w-4" />New Document
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Active Documents</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">{dashboardStats.activeDocuments}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Total Versions</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">{dashboardStats.totalVersions}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <History className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Languages</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">{dashboardStats.totalLanguages}</p>
                  </div>
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                  </div>
                </div>
              </Card>
              <Card className="p-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500">Last Updated</p>
                    <p className="text-lg sm:text-2xl font-semibold mt-1">
                      {dashboardStats.lastUpdated ? formatTimeAgo(dashboardStats.lastUpdated) : "Never"}
                    </p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>
            </div>
            <div className="flex overflow-x-auto gap-2 pb-2 snap-x relative z-10">
              {documentTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => setActiveTab(type.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap snap-start text-sm sm:text-base transition-colors ${activeTab === type.id ? "bg-blue-100 text-blue-700 border-2 border-blue-300" : "text-gray-600 hover:bg-gray-100 border border-transparent"}`}
                >
                  <type.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{type.name}</span>
                </button>
              ))}
              {additionalTabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg whitespace-nowrap snap-start text-sm sm:text-base transition-colors ${activeTab === tab.id ? "bg-blue-100 text-blue-700 border-2 border-blue-300" : "text-gray-600 hover:bg-gray-100 border border-transparent"}`}
                >
                  <tab.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
            <Card className="p-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4">
                  <div className="relative w-full sm:min-w-[200px] md:min-w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-8 pr-4 py-1 sm:pl-10 sm:py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <Button variant="outline" className="w-full sm:w-auto flex items-center gap-2 text-sm">
                    <Filter className="h-4 w-4 sm:h-5 sm:w-5" />Filters
                  </Button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  {(activeTab === ContentManagementTypeEnum.Customer_Information || activeTab === ContentManagementTypeEnum.Professional_Information) && (
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      className="w-full sm:w-auto px-2 py-1 sm:px-4 sm:py-2 border rounded-lg text-sm"
                    >
                      {countries.map(country => (
                        <option key={country.id} value={country.id}>{country.name}</option>
                      ))}
                    </select>
                  )}
                  <Button
                    variant="outline"
                    onClick={refreshData}
                    disabled={contentLoading}
                    className="w-full sm:w-auto flex items-center gap-2 text-sm"
                  >
                    <RefreshCw className={`h-4 w-4 ${contentLoading ? "animate-spin" : ""}`} />
                    {contentLoading ? "Refreshing..." : "Refresh"}
                  </Button>
                </div>
              </div>
            </Card>
            <div className="mt-4 sm:mt-6">{renderMainContent()}</div>
            {(activeTab !== "contact" && activeTab !== "menu" && activeTab !== "social") && (
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                {processedVersions
                  .filter(version => {
                    if (typeof activeTab === "string") {
                      // For special tabs like "contact", "menu", "social"
                      return version.type === activeTab;
                    } else {
                      // For content type enum values
                      return version.type === activeTab.toString();
                    }
                  })
                  .filter(version => (selectedLanguage === "all" || version.language.toLowerCase() === selectedLanguage))
                  .filter(version => (selectedCountry === "all" || !version.country || version.country === countries.find(c => c.id === selectedCountry)?.name))
                  .map(version => (
                    <VersionCard key={version.id} version={version} />
                  ))}
              </div>
            )}
          </div>
        </div>
      </main>
      {(showAddModal && activeTab !== "contact" && activeTab !== "menu" && activeTab !== "social") && (
        <DocumentModal version={selectedVersion} isOpen={true} onClose={() => { setShowAddModal(false); setSelectedVersion(null); }} />
      )}
      {showTranslationModal && (
        <TranslationModal
          contentItem={selectedContentForTranslation}
          translation={selectedTranslation}
          isOpen={true}
          onClose={() => {
            setShowTranslationModal(false);
            setSelectedTranslation(null);
            setSelectedContentForTranslation(null);
          }}
        />
      )}
      {showSuccessMessage && (
        <div className="fixed top-20 right-4 z-50 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5" />
            <span>{successMessage}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TermsEditor;